"use client";

import { useEffect, useState } from "react";
import { Menu, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex items-center gap-6">
        <div className="h-5 w-20 bg-muted/60 animate-pulse rounded-md" />
        <div className="h-5 w-20 bg-muted/60 animate-pulse rounded-md" />
        <div className="h-5 w-20 bg-muted/60 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="flex items-center gap-1 md:gap-2 lg:gap-3">
        {menuItems.map((item, i) => <NavigationMenuItem data-index={i} key={item.id} className="flex items-center">
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative font-mono text-[13px] lg:text-sm text-foreground hover:text-foreground hover:bg-transparent focus:bg-transparent px-3 py-2 tracking-widest group uppercase">
                <span className="relative inline-block">
                  {item.label}
                  <span className="pointer-events-none absolute left-0 right-0 -bottom-1 h-[2px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-[220ms] ease-out" style={{
                backgroundColor: "var(--accent)"
              }} />
                </span>
              </Link>
            </NavigationMenuLink>
            {i < menuItems.length - 1 && <span aria-hidden="true" className="ml-1 md:ml-2 lg:ml-3 inline-block h-1 w-1 rounded-md" style={{
          backgroundColor: "var(--accent)"
        }} />}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4">
        <div className="h-8 w-3/4 bg-muted/60 animate-pulse rounded-md" />
        <div className="h-8 w-2/3 bg-muted/60 animate-pulse rounded-md" />
        <div className="h-8 w-1/2 bg-muted/60 animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col">
      {menuItems.map((item, i) => <div data-index={i} key={item.id} className="border-b border-dashed py-4 flex items-center gap-4" style={{
      borderColor: "var(--border)"
    }}>
          <span className="font-mono text-xs tracking-widest" style={{
        color: "var(--accent)"
      }}>
            {String(i + 1).padStart(2, "0")}.
          </span>
          <Link to={item.href || "#"} className="font-default text-2xl font-bold text-foreground tracking-tighter flex-1 uppercase">
            {item.label}
          </Link>
        </div>)}
    </nav>;
}
export default function MainNavigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [{
      "@type": "Organization",
      "@id": "https://arborprotexas.com/#organization",
      "name": "Arbor Pro",
      "url": "https://arborprotexas.com/",
      "logo": {
        "@type": "ImageObject",
        "url": "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/ChatGPT Image Jun 6, 2026, 06_20_35 PM.png"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+1-817-503-3131",
        "contactType": "customer service",
        "areaServed": "US",
        "availableLanguage": "en"
      }
    }, {
      "@type": "LocalBusiness",
      "@id": "https://arborprotexas.com/#localbusiness",
      "name": "Arbor Pro",
      "image": "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/ChatGPT Image Jun 6, 2026, 06_20_35 PM.png",
      "telePhone": "(817) 503-3131",
      "url": "https://arborprotexas.com/",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Fort Worth Area",
        "addressLocality": "Fort Worth",
        "addressRegion": "TX",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "32.7555",
        "longitude": "-97.3308"
      },
      "areaServed": [{
        "@type": "City",
        "name": "Fort Worth"
      }, {
        "@type": "City",
        "name": "Dallas"
      }, {
        "@type": "State",
        "name": "Texas"
      }, {
        "@type": "AdministrativeArea",
        "name": "Tarrant County"
      }, {
        "@type": "AdministrativeArea",
        "name": "DFW Metroplex"
      }],
      "openingHoursSpecification": [{
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "08:00",
        "closes": "18:00"
      }],
      "category": ["Tree Care", "Arborist Services", "Tree Removal", "Tree Trimming"]
    }]
  };
  return <section data-section-id="1445" id="main-navigation" className="sticky top-0 z-50 w-full transition-all duration-[240ms] ease-out bg-background text-foreground" style={{
    backgroundColor: scrolled ? "hsl(165 10% 92% / 0.9)" : "hsl(165 10% 92%)",
    backdropFilter: scrolled ? "blur(8px)" : "blur(0px)",
    WebkitBackdropFilter: scrolled ? "blur(8px)" : "blur(0px)"
  }}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(schemaData)
    }} />
      <div className="relative">
        <div className={`hidden md:flex items-center justify-between px-6 md:px-10 lg:px-16 font-mono text-[10px] tracking-widest uppercase overflow-hidden transition-all duration-[240ms] ease-out ${scrolled ? "max-h-0 opacity-0" : "max-h-8 opacity-100 py-1.5"}`} style={{
        color: "var(--secondary)"
      }} aria-hidden="true">
          <span>SERVING — DFW TREE CARE SPECIALISTS</span>
          <span style={{
          color: "var(--accent)"
        }}>32°45′N · 97°19′W</span>
        </div>

        <div className="px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-12 items-center h-[72px] md:h-[84px] gap-4">
            <div className="col-span-6 md:col-span-3 flex items-center gap-3 min-w-0">
              <Link to="/" className="flex items-center gap-3 min-w-0" aria-label="Arbor Pro — Home">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/ChatGPT Image Jun 6, 2026, 06_20_35 PM.png" alt="Arbor Pro Logo" className="h-16 md:h-20 lg:h-24 w-auto shrink-0" loading="lazy" />
              </Link>
              <span aria-hidden="true" className={`hidden lg:inline-block font-mono text-[10px] tracking-widest uppercase whitespace-nowrap transition-opacity duration-[240ms] ease-out ${scrolled ? "opacity-100" : "opacity-0"}`} style={{
              color: "var(--secondary)"
            }}>
                Arbor Pro Tree Service
              </span>
            </div>

            <div className="hidden md:flex md:col-span-6 items-center justify-center">
              <MenuProvider menu_id="45" className="w-full flex justify-center">
                <DesktopMenuConsumer />
              </MenuProvider>
            </div>

            <div className="col-span-6 md:col-span-3 flex items-center justify-end gap-3">
              <Link to="tel:(817) 503-3131" className="hidden md:inline-flex group">
                <Button className="font-mono text-[11px] tracking-widest uppercase px-4 py-4 h-auto rounded-md text-accent-foreground shadow-md transition-all duration-[220ms] ease-out hover:opacity-90" style={{
                backgroundColor: "var(--accent)"
              }}>
                  <Phone className="size-3.5 mr-2" />
                  <span>(817) 503-3131</span>
                </Button>
              </Link>

              <Link to="/GetQuotePage" className="hidden md:inline-flex group">
                <Button className="relative font-mono text-[11px] tracking-widest uppercase px-8 py-4 h-auto rounded-md text-primary-foreground shadow-md transition-all duration-[220ms] ease-out group-hover:bg-accent group-hover:text-accent-foreground" style={{
                backgroundColor: "var(--primary)"
              }}>
                  <span aria-hidden="true" className="pointer-events-none absolute inset-[2px] rounded-md" style={{
                  border: "1px solid var(--accent)"
                }} />
                  <span className="relative z-10">Get Quote</span>
                </Button>
              </Link>

              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-transparent focus:bg-transparent rounded-md" aria-label="Open menu">
                    <Menu className="size-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col border-l-0" style={{
                backgroundColor: "var(--background)"
              }}>
                  <SheetHeader className="px-6 pt-6 pb-4 space-y-3">
                    <div className="font-mono text-[11px] tracking-widest uppercase flex items-center justify-between" style={{
                    color: "var(--secondary)"
                  }}>
                      <span>
                        <span style={{
                        color: "var(--accent)"
                      }}>§</span> MENU
                      </span>
                      <span style={{
                      color: "var(--accent)"
                    }}>32°45′N</span>
                    </div>
                    <SheetTitle asChild>
                      <div className="font-default text-3xl font-extrabold tracking-tighter text-foreground uppercase">
                        Arbor{" "}
                        <span className="relative">
                          Pro
                          <span className="absolute left-0 right-0 -bottom-1 h-[3px]" style={{
                          backgroundColor: "var(--accent)"
                        }} />
                        </span>
                      </div>
                    </SheetTitle>
                    <div className="h-px w-full" style={{
                    backgroundImage: "repeating-linear-gradient(to right, var(--primary) 0, var(--primary) 4px, transparent 4px, transparent 8px)"
                  }} />
                  </SheetHeader>

                  <div className="flex-1 overflow-y-auto px-6 py-2">
                    <MenuProvider menu_id="45">
                      <MobileMenuConsumer />
                    </MenuProvider>
                  </div>

                  <div className="px-6 py-6 border-t border-dashed space-y-4" style={{
                  borderColor: "var(--border)"
                }}>
                    <Link to="/GetQuotePage" className="block">
                      <Button className="relative w-full font-mono text-[11px] tracking-widest uppercase px-7 py-5 h-auto rounded-md text-primary-foreground" style={{
                      backgroundColor: "var(--primary)"
                    }}>
                        <span aria-hidden="true" className="pointer-events-none absolute inset-[2px] rounded-md" style={{
                        border: "1px solid var(--accent)"
                      }} />
                        <span className="relative z-10">Request Service</span>
                      </Button>
                    </Link>
                    <div className="font-mono text-[10px] tracking-widest uppercase text-center" style={{
                    color: "var(--secondary)"
                  }}>
                      Fort Worth · DFW · Est 2014
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>

        <div aria-hidden="true" className="absolute left-0 right-0 bottom-0 transition-all duration-[240ms] ease-out" style={{
        height: scrolled ? "2px" : "1px",
        backgroundColor: scrolled ? "var(--accent)" : "var(--border)"
      }} />
      </div>
    </section>;
}